import { useRouter } from 'next/router';
import { useQuery } from '@apollo/client';
import { ShopView } from '@/components/Shop/ShopPage';
import { ShopInfoSkeleton } from '@/components/Shop/ShopSkeleton/ShopInfoSkeleton';
import useScreenSize from '@/hooks/useScreenSize';
import { Footer } from '@/components/Footer/Footer';
import { Header } from '@/components/Header/Header';
import { DesktopFooter } from '@/components/Footer/DesktopFooter';
import NoSsr from '@/components/NoSsr/NoSsr';
import { GET_SHOP_DETAILS } from '@/api/queries/shop.queries';
import type { ShopDetails } from '@/api/types/shop.types';
import { ShopSearchProvider } from '@/contexts/ShopSearchContext';
import { ErrorView } from '@/views/Error/Error.view';

interface ShopViewPageViewProps {
  shopDetails?: ShopDetails;
}
interface GetShopDetailsResponse {
  ShopDetails: ShopDetails;
}

export const ShopViewPageView: React.FC<ShopViewPageViewProps> = ({
  shopDetails,
}) => {
  const { isLarge } = useScreenSize();
  const { query } = useRouter();
  const slug = String(query.slug ?? '');
  const { data, error, loading } = useQuery<GetShopDetailsResponse>(
    GET_SHOP_DETAILS,
    {
      variables: { slug },
      skip: !slug,
      errorPolicy: 'all',
      fetchPolicy: shopDetails ? 'cache-first' : 'network-only',
    }
  );

  const details = shopDetails || data?.ShopDetails;

  const showError =
    !shopDetails &&
    !loading &&
    !details &&
    (error || (data !== undefined && data?.ShopDetails === null));

  const userLegacyId = details?.user?.legacyId;

  if (showError) {
    return (
      <NoSsr>
        <ErrorView statusCode={404} />
      </NoSsr>
    );
  }

  return (
    <ShopSearchProvider userLegacyId={userLegacyId}>
      <Header />
      {details ? (
        <ShopView data={details} />
      ) : (
        <NoSsr>
          <ShopInfoSkeleton />
        </NoSsr>
      )}
      <NoSsr>{isLarge ? <DesktopFooter /> : <Footer />}</NoSsr>
    </ShopSearchProvider>
  );
};
